import { useState } from "react";
import { useCertifiedPayroll, useCertifiedPayrollWeeks, useSubmitCertifiedPayroll, CertifiedPayrollRow } from "@/hooks/useCertifiedPayroll";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Download, ShieldCheck, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";

const fmt = (n: number) => `$${n.toLocaleString("en-US", { minimumFractionDigits: 2 })}`;
const maskSSN = (last4: string | null) => last4 ? `XXX-XX-${last4}` : "—";

interface Props {
  projectId: string;
  personnelProjectId?: string;
  companyId?: string;
}

export const ProjectCertifiedPayrollTab = ({ projectId, personnelProjectId, companyId }: Props) => {
  const [selectedWeek, setSelectedWeek] = useState<string>("");
  const [certified, setCertified] = useState(false);

  const { data: weeks } = useCertifiedPayrollWeeks(personnelProjectId);
  const { data: rows, isLoading } = useCertifiedPayroll(personnelProjectId, selectedWeek || undefined);
  const submitCert = useSubmitCertifiedPayroll();

  const handleExportCSV = () => {
    if (!rows?.length) return;
    const headers = [
      "Employee Name", "SSN (Last 4)", "Work Classification",
      "Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun",
      "Total Hours", "Regular Hours", "OT Hours",
      "Rate of Pay", "OT Rate", "Regular Wages", "OT Wages",
      "Gross Wages", "Deductions", "Net Pay"
    ];
    const csvRows = rows.map((r) => [
      r.employee_name,
      maskSSN(r.tax_id_last4),
      r.trade_classification || "",
      r.mon_hours, r.tue_hours, r.wed_hours, r.thu_hours,
      r.fri_hours, r.sat_hours, r.sun_hours,
      r.total_hours, r.regular_hours, r.ot_hours,
      r.hourly_rate, r.ot_rate,
      r.regular_wages, r.ot_wages, r.gross_wages,
      r.deductions, r.net_pay
    ]);
    const csv = [headers, ...csvRows].map((r) => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `WH-347_CertifiedPayroll_${selectedWeek || "all"}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleSubmitCertification = async () => {
    if (!certified) {
      toast.error("You must certify the statement before submitting.");
      return;
    }
    if (!selectedWeek || !personnelProjectId || !companyId) return;
    try {
      await submitCert.mutateAsync({
        companyId,
        projectId,
        personnelProjectId,
        weekEnding: selectedWeek,
      });
      toast.success("Certified payroll submitted for week ending " + selectedWeek);
      setCertified(false);
    } catch (e: any) {
      toast.error(e.message || "Failed to submit certification");
    }
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <ShieldCheck className="h-5 w-5 text-primary" />
                Certified Payroll Report (WH-347)
              </CardTitle>
              <CardDescription>
                Davis-Bacon Act — Weekly certified payroll for federally-funded public works projects
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Select value={selectedWeek} onValueChange={setSelectedWeek}>
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="Select week ending" />
                </SelectTrigger>
                <SelectContent>
                  {(weeks ?? []).map((w) => (
                    <SelectItem key={w} value={w}>
                      WE {format(new Date(w + "T00:00:00"), "MMM d, yyyy")}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button variant="outline" size="sm" onClick={handleExportCSV} disabled={!rows?.length}>
                <Download className="mr-1 h-4 w-4" /> CSV
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <p className="text-muted-foreground">Loading…</p>
          ) : !rows?.length ? (
            <div className="flex flex-col items-center gap-2 py-8 text-muted-foreground">
              <AlertTriangle className="h-8 w-8" />
              <p>{selectedWeek ? "No payroll data for selected week." : "Select a week to view payroll data."}</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto rounded border">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/50">
                      <TableHead className="font-semibold">Employee Name</TableHead>
                      <TableHead className="font-semibold">SSN (Last 4)</TableHead>
                      <TableHead className="font-semibold">Classification</TableHead>
                      <TableHead className="text-center font-semibold">M</TableHead>
                      <TableHead className="text-center font-semibold">T</TableHead>
                      <TableHead className="text-center font-semibold">W</TableHead>
                      <TableHead className="text-center font-semibold">Th</TableHead>
                      <TableHead className="text-center font-semibold">F</TableHead>
                      <TableHead className="text-center font-semibold">Sa</TableHead>
                      <TableHead className="text-center font-semibold">Su</TableHead>
                      <TableHead className="text-right font-semibold">Total</TableHead>
                      <TableHead className="text-right font-semibold">Rate</TableHead>
                      <TableHead className="text-right font-semibold">Gross</TableHead>
                      <TableHead className="text-right font-semibold">Deductions</TableHead>
                      <TableHead className="text-right font-semibold">Net Pay</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rows.map((r) => (
                      <TableRow key={r.line_id}>
                        <TableCell className="font-medium">{r.employee_name}</TableCell>
                        <TableCell className="font-mono text-sm">{maskSSN(r.tax_id_last4)}</TableCell>
                        <TableCell>
                          {r.trade_classification ? (
                            <Badge variant="secondary">{r.trade_classification}</Badge>
                          ) : (
                            <span className="text-muted-foreground text-xs">—</span>
                          )}
                        </TableCell>
                        <TableCell className="text-center">{r.mon_hours || "—"}</TableCell>
                        <TableCell className="text-center">{r.tue_hours || "—"}</TableCell>
                        <TableCell className="text-center">{r.wed_hours || "—"}</TableCell>
                        <TableCell className="text-center">{r.thu_hours || "—"}</TableCell>
                        <TableCell className="text-center">{r.fri_hours || "—"}</TableCell>
                        <TableCell className="text-center">{r.sat_hours || "—"}</TableCell>
                        <TableCell className="text-center">{r.sun_hours || "—"}</TableCell>
                        <TableCell className="text-right font-medium">{r.total_hours}</TableCell>
                        <TableCell className="text-right">{fmt(r.hourly_rate)}</TableCell>
                        <TableCell className="text-right font-medium">{fmt(r.gross_wages)}</TableCell>
                        <TableCell className="text-right">{fmt(r.deductions)}</TableCell>
                        <TableCell className="text-right font-medium">{fmt(r.net_pay)}</TableCell>
                      </TableRow>
                    ))}
                    {/* Totals row */}
                    <TableRow className="bg-muted/30 font-semibold">
                      <TableCell colSpan={3}>Totals</TableCell>
                      <TableCell className="text-center">{rows.reduce((s, r) => s + r.mon_hours, 0)}</TableCell>
                      <TableCell className="text-center">{rows.reduce((s, r) => s + r.tue_hours, 0)}</TableCell>
                      <TableCell className="text-center">{rows.reduce((s, r) => s + r.wed_hours, 0)}</TableCell>
                      <TableCell className="text-center">{rows.reduce((s, r) => s + r.thu_hours, 0)}</TableCell>
                      <TableCell className="text-center">{rows.reduce((s, r) => s + r.fri_hours, 0)}</TableCell>
                      <TableCell className="text-center">{rows.reduce((s, r) => s + r.sat_hours, 0)}</TableCell>
                      <TableCell className="text-center">{rows.reduce((s, r) => s + r.sun_hours, 0)}</TableCell>
                      <TableCell className="text-right">{rows.reduce((s, r) => s + r.total_hours, 0)}</TableCell>
                      <TableCell />
                      <TableCell className="text-right">{fmt(rows.reduce((s, r) => s + r.gross_wages, 0))}</TableCell>
                      <TableCell className="text-right">{fmt(rows.reduce((s, r) => s + r.deductions, 0))}</TableCell>
                      <TableCell className="text-right">{fmt(rows.reduce((s, r) => s + r.net_pay, 0))}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>

              {/* Certification Statement */}
              <div className="mt-6 rounded-lg border border-primary/30 bg-primary/5 p-4 space-y-3">
                <h3 className="font-semibold text-sm">Certification Statement</h3>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  I, the undersigned, certify under penalty of perjury that the information contained herein
                  is true, accurate, and complete to the best of my knowledge. I certify that all persons
                  employed on this project during the payroll period have been paid the full weekly wages earned,
                  that no rebates have been or will be made either directly or indirectly to or on behalf of
                  the contractor from the full weekly wages earned by any person, and that no deductions have
                  been made either directly or indirectly from the full wages earned by any person, other than
                  permissible deductions as defined in Regulations, Part 3 (29 CFR Subtitle A), of the
                  Copeland Act (40 U.S.C. § 3145).
                </p>
                <label className="flex items-start gap-2 cursor-pointer">
                  <Checkbox
                    checked={certified}
                    onCheckedChange={(v) => setCertified(!!v)}
                    className="mt-0.5"
                  />
                  <span className="text-sm font-medium">
                    I certify the above statement is true and accurate
                  </span>
                </label>
                <Button
                  onClick={handleSubmitCertification}
                  disabled={!certified || submitCert.isPending}
                  className="w-full sm:w-auto"
                >
                  {submitCert.isPending ? "Submitting…" : "Submit Certified Payroll"}
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
